/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { signOut } from "aws-amplify/auth"
import {
  Eye,
  LogOut,
  MessageSquareWarning,
  Moon,
  Shield,
  Sun,
  User,
} from "lucide-react"
import { useState, type FC } from "react"
import { useNavigate } from "react-router"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import { useAuth } from "../../hooks/useAuth"
import { useThemeMode } from "../../hooks/useThemeMode"
import { ADMIN_ROLE, roleLabel } from "../../containers/AdminView/constants"
import { AuthenticationService } from "../../services/authentication"
import { isImpersonating } from "../../services/impersonation"
import { ViewAsDialog } from "../../containers/ViewAs/ViewAsDialog"
import { borders } from "../../styles"
import { theme } from "../../styles/theme/theme"

const colors = theme.colors

const headerStyles = {
  header: css`
    flex-shrink: 0;
    z-index: 20;
    background-color: ${theme.colors.background};
    ${borders.bottom}
    padding: 0 ${theme.spacing.md};
    height: ${theme.spacing["2xl"]};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  `,
  profileBtn: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.3rem;
    padding: 0.25rem 0.5rem;
    font-size: 12px;
    font-weight: 400;
    cursor: pointer;
    border: none;
    outline: none;
    transition: background-color 0.15s, color 0.15s;
    background-color: transparent;
    color: ${colors.mutedForeground};
    &:hover {
      color: ${colors.blue600};
    }
  `,
  userInfo: css`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.5rem 0.5rem 0.375rem;
  `,
  userInfoRow: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  userAvatar: css`
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  `,
  userName: css`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${colors.foreground};
  `,
  userEmail: css`
    font-size: 0.75rem;
    color: ${colors.mutedForeground};
    margin-left: 2.5rem;
  `,
  userRole: css`
    display: block;
    font-size: 0.75rem;
    color: ${colors.mutedForeground};
    margin-left: 2.5rem;
    margin-top: 0.125rem;
  `,
}

/** Account dropdown (avatar + email, light/dark toggle, log out). Extracted so
 *  both the full RunHeader and the slim chromeless header can reuse it. */
export const AccountMenu: FC = () => {
  const { user } = useAuth()
  const { mode, toggleMode } = useThemeMode()
  const navigate = useNavigate()
  const isAdmin = user?.role === ADMIN_ROLE
  const [viewAsOpen, setViewAsOpen] = useState(false)
  const impersonating = isImpersonating()

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button css={headerStyles.profileBtn} title="Profile">
          <User size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end">
        <DropdownMenuLabel css={headerStyles.userInfo}>
          <div css={headerStyles.userInfoRow}>
            {user?.picture ? (
              <img src={user.picture} alt="" css={headerStyles.userAvatar} />
            ) : (
              <div
                css={[
                  headerStyles.userAvatar,
                  css`
                    background: ${colors.muted};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `,
                ]}
              >
                <User size={14} color={colors.mutedForeground} />
              </div>
            )}
            <span css={headerStyles.userName}>
              {user?.email ?? user?.name ?? "User"}
            </span>
          </div>
          {user?.role && (
            <span css={headerStyles.userRole}>Role: {roleLabel(user.role)}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            // Keep the menu open so the theme flip is visible in place
            e.preventDefault()
            toggleMode()
          }}
        >
          {mode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          {mode === "dark" ? "Light mode" : "Dark mode"}
        </DropdownMenuItem>
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/user-admin")}>
              <Shield size={16} />
              User Admin
            </DropdownMenuItem>
            {!impersonating && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  setViewAsOpen(true)
                }}
              >
                <Eye size={16} />
                View As
              </DropdownMenuItem>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => {
            AuthenticationService.logout();
            signOut();
          }}
        >
          <LogOut size={16} />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />
    </>
  )
}

/** The speech-bubble feedback trigger. Extracted alongside AccountMenu so the
 *  slim header can carry the same two account/feedback controls. */
export const FeedbackButton: FC<{ onSendFeedback?: () => void }> = ({
  onSendFeedback,
}) => (
  <button
    type="button"
    css={headerStyles.profileBtn}
    title="Send feedback"
    aria-label="Send feedback"
    onClick={() => onSendFeedback?.()}
  >
    <MessageSquareWarning size={16} />
  </button>
)

interface RunHeaderProps {
  /** Open the feedback modal (state lives in DashboardLayout). Relocated here
   *  from the sidebar so feedback sits in the top navbar, right after the
   *  account icon. */
  onSendFeedback?: () => void
}

export const RunHeader: FC<RunHeaderProps> = ({ onSendFeedback }) => (
  <header css={headerStyles.header}>
    <AccountMenu />
    <FeedbackButton onSendFeedback={onSendFeedback} />
  </header>
)
